import { imageExtension, KEPT_AS_IS, MAX_IMAGE_WIDTH } from "./images.ts";

/** A picture ready to keep: its bytes, its file extension and a fingerprint of the original. */
export interface Prepared {
  blob: Blob;
  ext: string;
  hash: string;
}

/** The first eight hex digits of the SHA-256 of a file. */
export async function fingerprint(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest).slice(0, 4)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Draws a bitmap at the given width onto a canvas and encodes it. */
async function encode(bitmap: ImageBitmap, width: number, type: string): Promise<Blob | null> {
  const height = Math.max(1, Math.round((bitmap.height * width) / bitmap.width));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, width, height);
  return new Promise((resolve) => canvas.toBlob(resolve, type, 0.85));
}

/**
 * Scales a picture down to at most 1600 px wide and re-encodes it as WebP.
 * GIFs and SVGs are kept exactly as they are. A browser that cannot write WebP
 * (Safari) keeps a PNG or JPEG instead, or the original when it is already small.
 * Returns null for a file that is not a picture Quire keeps.
 */
export async function preparePicture(file: File, mime: string): Promise<Prepared | null> {
  const ext = imageExtension(mime);
  if (!ext) return null;
  const hash = await fingerprint(file);
  if (KEPT_AS_IS.has(mime)) return { blob: file, ext, hash };

  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return null;
  try {
    const width = Math.min(bitmap.width, MAX_IMAGE_WIDTH);
    const webp = await encode(bitmap, width, "image/webp");
    if (webp?.type === "image/webp") {
      // Never make a small picture bigger than it was.
      if (width === bitmap.width && webp.size >= file.size && mime === "image/webp") {
        return { blob: file, ext: "webp", hash };
      }
      return { blob: webp, ext: "webp", hash };
    }
    // No WebP encoder: keep the original if it needs no shrinking, else a PNG or JPEG.
    if (width === bitmap.width) return { blob: file, ext, hash };
    const fallbackType = mime === "image/png" ? "image/png" : "image/jpeg";
    const fallback = await encode(bitmap, width, fallbackType);
    if (!fallback) return null;
    return { blob: fallback, ext: fallbackType === "image/png" ? "png" : "jpg", hash };
  } finally {
    bitmap.close();
  }
}
